import { Form } from "antd";
import { Message, Modal, Spin } from "../../../BP2Components";
import React, { memo, useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { handleChangedRelatedField, renderDevInfo, handleFieldAlert } from "../../GeneralScripts";
import PropTypes from "prop-types";
import "./SqlField.less";
import SuperSelect from "../../../SuperSelect/SuperSelect.jsx";
import FormComplete from "../../FormComplete";
import { addSubformData } from "../../../../services/api/Api";
import { useFormContext } from "../../../../context/FormContext/FormContext";
import { transformFormValuesForRequest } from "../../GeneralScripts";
import { setSqlValues } from "../../../../appRedux/actions/FormsV2";
import FieldInfo from "../FieldInfo/FieldInfo";
import { getAllFieldsWithAlerts, handleTemplateAlert } from "../../../../util/Scripts";

const SqlField = memo(
   ({
      isRequired,
      element,
      form,
      isVisible,
      disabled,
      rowDataId,
      insideGrid = false,
      setChangelogData,
      formId,
      initialValue,
   }) => {
      const [isOpen, setIsOpen] = useState(false);
      const [subFormData, setSubFormData] = useState();
      const [options, setOptions] = useState([]);
      const [infoText, setInfoText] = useState(null);

      const { sqlValues, gridSqlValues } = useSelector(({ formsv2 }) => formsv2);

      const {
         formData,
         changeFieldPendingStatus,
         fieldsInfo,
         setFieldsInfo,
         fieldOptions,
         reloadFieldOptions,
         gridFormKeys,
         refreshForm,
         fieldEnumMap,
      } = useFormContext();

      const { getFieldDecorator } = form;

      const dispatch = useDispatch();
      const intl = useIntl();

      const infoClass =
         fieldsInfo[element.tech_name] && fieldsInfo[element.tech_name] !== ""
            ? "sql-field-info"
            : "";

      const lastOptionsHashRef = useRef({});
      const autoSelectedValueRef = useRef({});
      const lastDefaultLoggedRef = useRef({});

      const [autoSelectRelationsTick, setAutoSelectRelationsTick] = useState(0);

      useEffect(() => {
         const techName = element.tech_name;
         const fieldOptionsValue = fieldOptions?.[techName];
         if (fieldOptionsValue == null) return;

         const newOptions = fieldOptionsValue.map((option) => {
            const value = option.value;
            const label =
               option.label !== null
                  ? option.label !== undefined
                     ? option.label
                     : option.value
                  : "";
            return { value, label, isPlaceholder: false };
         });

         const optionsHash = JSON.stringify(newOptions);

         if (lastOptionsHashRef.current[techName] === optionsHash) return;
         lastOptionsHashRef.current[techName] = optionsHash;
         if (newOptions.length > 0 && element.object.UIP_NUM4 === "1") {
            const emptyLabel = element.object.UIP_TEKST3;
            if (isVisible(element) && emptyLabel) {
               setChangelogData?.(techName, emptyLabel);
            }
         }

         dispatch(setSqlValues({ [techName]: newOptions }));
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
         fieldOptions,
         element.tech_name,
         element.object.UIP_NUM4,
         element.object.UIP_TEKST3,
         dispatch,
      ]);

      // we are not translating forms so far, but if we need to, just add here a proper translation
      const translatedMessage = intl.formatMessage({ id: "Dodaj opcję" });

      const handleSelect = (value, actionMeta) => {
         if (value?.value === translatedMessage) {
            getSubformData();
         } else {
            form.setFieldsValue({ [element.tech_name]: value.value });
            const isSelect = actionMeta?.action === "select-option";

            if (
               isVisible(element) &&
               String(value?.value) !== "-1" &&
               setChangelogData &&
               isSelect
            ) {
               setChangelogData(element.tech_name, value.label, element.label);
            }
            const allFieldsWithAlerts = getAllFieldsWithAlerts(formData);
            handleChangedRelatedField(
               element,
               form,
               setFieldsInfo,
               allFieldsWithAlerts,
               formId,
               intl,
               insideGrid ? gridFormKeys[rowDataId] : formData._formKey,
               rowDataId,
               changeFieldPendingStatus,
               fieldEnumMap,
            );
            element.hasAlert &&
               handleFieldAlert(
                  element.id,
                  element.tech_name,
                  form,
                  formData._formKey,
                  setFieldsInfo,
                  fieldEnumMap,
               );
         }
      };

      const translatedLabel = element.label;

      const getEmptyValue = () => {
         let defaultValue = { value: "", label: "" };
         if (element.object.UIP_NUM4 === "1") {
            defaultValue = {
               value: element.object.UIP_TEKST3 || "",
               label: element.object.UIP_TEKST3,
               isPlaceholder: true,
            };
         }
         return defaultValue;
      };

      const getDefaultValue = () => {
         const normalize = (val) => {
            if (typeof val === "string") {
               return { value: val, label: val, isPlaceholder: false };
            }
            if (val && typeof val === "object") {
               return {
                  value: val.value ?? val.label ?? "",
                  label: val.label ?? val.value ?? "",
                  isPlaceholder: Boolean(val.isPlaceholder),
               };
            }
            return { value: "", label: "", isPlaceholder: false };
         };

         let defaultValue;

         if (initialValue) {
            // 1) initialValue prop
            defaultValue = normalize(initialValue);
         } else if (element.value) {
            // 2) element.value
            defaultValue = normalize(element.value);
         } else if (element.object?.UIP_NUM4 === "1") {
            // 3) special UIP_TEKST fallback
            defaultValue = normalize({
               value: element.object.UIP_TEKST3,
               label: element.object.UIP_TEKST3,
               isPlaceholder: true,
            });
         } else {
            // 4) nothing to show
            defaultValue = { value: "", label: "" };
         }

         return defaultValue;
      };

      useEffect(() => {
         const defaultValue = getDefaultValue();
         if (isVisible(element) && defaultValue.label) {
            const tech = element.tech_name;
            if (lastDefaultLoggedRef.current[tech] === defaultValue.label) return;
            lastDefaultLoggedRef.current[tech] = defaultValue.label;
            setChangelogData?.(tech, defaultValue.label);
         }
         // isVisible and setChangelogData are stable references, element is covered by its properties
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
         initialValue,
         element.value,
         element.object?.UIP_NUM4,
         element.object?.UIP_TEKST3,
         element.tech_name,
      ]);

      const handleAddOption = (options) => {
         let newOption = {
            value: translatedMessage,
            label: translatedMessage,
            isDisabled: true,
            isPlaceholder: false,
         };

         return [...options, newOption];
      };

      useEffect(() => {
         if (gridSqlValues?.[rowDataId]?.[element.tech_name]?.length > 0) {
            const availableOptions = gridSqlValues[rowDataId][element.tech_name].map(
               (singleOption) => ({
                  value: singleOption.value || singleOption.label,
                  label: singleOption.label || "",
                  isPlaceholder: false,
               }),
            );

            const optionsWithEmpty =
               element.object?.UIP_NUM4 === "1"
                  ? [
                       {
                          value: element.object.UIP_TEKST3 || "",
                          label: element.object.UIP_TEKST3,
                          isPlaceholder: true,
                       },
                       ...availableOptions,
                    ]
                  : availableOptions;

            if (!isNaN(parseInt(element?.num1)) && parseInt(element?.num1) !== 0) {
               setOptions(handleAddOption(optionsWithEmpty));
            } else {
               if (
                  optionsWithEmpty.every(
                     (option) => option.value !== undefined && option.label !== undefined,
                  ) &&
                  optionsWithEmpty.length > 0
               ) {
                  setOptions(optionsWithEmpty);
               }
            }
         } else if (Array.isArray(sqlValues?.[element.tech_name])) {
            if (sqlValues[element.tech_name].length === 0) {
               setOptions((prev) => (prev.length === 0 ? prev : []));
            } else {
               const availableOptions = sqlValues[element.tech_name].map((singleOption) => ({
                  value: singleOption.value || singleOption.label,
                  label: singleOption.label || "",
                  isPlaceholder: false,
               }));

               const optionsWithEmpty =
                  element.object?.UIP_NUM4 === "1"
                     ? [
                          {
                             value: element.object.UIP_TEKST3 || "",
                             label: element.object.UIP_TEKST3,
                             isPlaceholder: true,
                          },
                          ...availableOptions,
                       ]
                     : availableOptions;

               if (!isNaN(parseInt(element?.num1)) && parseInt(element?.num1) !== 0) {
                  setOptions(handleAddOption(optionsWithEmpty));
               } else {
                  if (
                     optionsWithEmpty.every(
                        (option) => option.value !== undefined && option.label !== undefined,
                     ) &&
                     optionsWithEmpty.length > 0
                  ) {
                     setOptions(optionsWithEmpty);
                  }
               }
            }
         }
      }, [sqlValues, gridSqlValues, element.object?.UIP_NUM4]);

      // this function could be exported to formScripts #TodoContextFeature
      const getSubformData = () => {
         let formRequestBody = {
            ...transformFormValuesForRequest(form.getFieldsValue(), null, undefined, fieldEnumMap),
            UIF_ID: formId,
            easyForm: true,
         };
         if (formData._formKey) {
            formRequestBody["_formKey"] = formData._formKey;
         }
         try {
            Message.loading(intl.formatMessage({ id: "form.isProcessing" }), 0);
            changeFieldPendingStatus && changeFieldPendingStatus(true);
            addSubformData(parseInt(element?.num1), formRequestBody)
               .then((res) => {
                  Message.destroy();
                  setSubFormData(res?.data?.getSubformData?.mainForm);
                  setIsOpen(true);
               })
               .finally(() => {
                  changeFieldPendingStatus && changeFieldPendingStatus(false);
               });
         } catch (err) {
            console.log(err);
         }
      };

      useEffect(() => {
         const infoText = fieldsInfo[element.tech_name];
         setInfoText(infoText);
         handleTemplateAlert(element.tech_name, infoText);
      }, [fieldsInfo[element.tech_name]]);

      // Auto-select single option: sets the value and triggers tick for related field handling
      useEffect(() => {
         if (disabled) return;
         if (options.length !== 1) return;

         const singleOption = options[0];
         if (!singleOption || singleOption.isPlaceholder || singleOption?.isDisabled) return;

         const techName = element.tech_name;
         const nextValue = String(singleOption.value ?? "");
         if (!nextValue) return;

         if (autoSelectedValueRef.current[techName] === nextValue) return;

         const current = String(form.getFieldValue(techName) ?? "");
         if (current === nextValue) {
            autoSelectedValueRef.current[techName] = nextValue;
            return;
         }

         autoSelectedValueRef.current[techName] = nextValue;

         form.setFieldsValue({ [techName]: singleOption.value });
         setChangelogData?.(techName, singleOption.label, element.label);

         setAutoSelectRelationsTick((prev) => prev + 1);
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [options, disabled, element.tech_name]);

      useEffect(() => {
         if (autoSelectRelationsTick === 0) return;

         const allFieldsWithAlerts = getAllFieldsWithAlerts(formData);

         handleChangedRelatedField(
            element,
            form,
            setFieldsInfo,
            allFieldsWithAlerts,
            formId,
            intl,
            insideGrid ? gridFormKeys[rowDataId] : formData._formKey,
            rowDataId,
            changeFieldPendingStatus,
            fieldEnumMap,
         );

         if (element.hasAlert) {
            handleFieldAlert(
               element.id,
               element.tech_name,
               form,
               formData._formKey,
               setFieldsInfo,
               fieldEnumMap,
            );
         }
         // Intentionally only trigger on tick change to prevent loops - other deps are stable or handled by tick
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [autoSelectRelationsTick]);

      return (
         <>
            {/* subform modal */}
            <Modal isOpen={isOpen} onCancel={setIsOpen}>
               <FormComplete
                  formData={subFormData}
                  additionalSaveData={{
                     _parent: {
                        ...transformFormValuesForRequest(
                           form.getFieldsValue(),
                           null,
                           undefined,
                           fieldEnumMap,
                        ),
                        UIF_ID: formId,
                        easyForm: true,
                     },
                  }}
                  hideAttachments={true}
                  saveCallback={refreshForm}
               />
            </Modal>
            {/* SQL field */}
            <Form.Item
               key={element.tech_name}
               label={!insideGrid && translatedLabel}
               className={`${element?.type}-field ${
                  element.obow === "1" ? "sql-field-required" : ""
               }`}
               style={{
                  display: !isVisible(element) && !window.devLogged ? "none" : "",
                  gridArea: element.tech_name.replaceAll("#", "_"),
                  opacity: isVisible(element) ? "1" : "0.5",
               }}
               data-extraButtons={element.FormLoContent.seeking_out_type === "3"}
               name={element.tech_name}
            >
               {getFieldDecorator(element.tech_name, {
                  rules: [
                     {
                        required: isRequired(element),
                        message: intl.formatMessage({ id: "field.required" }),
                     },
                  ],
                  initialValue: getDefaultValue()?.value || "",
               })}

               {!getDefaultValue() ? (
                  <Spin />
               ) : (
                  <>
                     <SuperSelect
                        options={options}
                        disabled={disabled}
                        isLoading={options.length === 0 && !disabled}
                        handleSelect={handleSelect}
                        seekingOutType={element.FormLoContent.seeking_out_type}
                        form={form}
                        listId={
                           element.FormLoContent.dedicated_list_value ||
                           element.FormLoContent.advansed_search_list_id
                        }
                        techname={element.tech_name}
                        name={element.tech_name}
                        className={`sql-field__select field_${element.tech_name} ${infoClass} has-alert-${element.hasAlert} _type_${element?.type}_type_ _id_${element.id}_id_`}
                        defaultValue={getDefaultValue(options)}
                        refreshForm={refreshForm}
                        reloadFieldOptions={reloadFieldOptions}
                     />
                  </>
               )}
               {renderDevInfo(element)}
               <FieldInfo text={infoText} />
            </Form.Item>
         </>
      );
   },
);

SqlField.propTypes = {
   element: PropTypes.object.isRequired,
   disabled: PropTypes.bool,
   isVisible: PropTypes.func,
   form: PropTypes.any,
   formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   insideGrid: PropTypes.bool,
   initialValue: PropTypes.string,
};

export default SqlField;
